import React, { useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { UniqueIdentifier } from '@dnd-kit/core'
import { Note } from '@shared/types'
import { createEmptySheetContent } from '@shared/services/sheetNoteFormat'

// 首屏 bundle 瘦身：侧边栏与面板组件懒加载（移动端首屏均不显示，本地 chunk 加载极快）
const SortableTree = lazy(() => import('@components/Sidebar/SortableTree').then(m => ({ default: m.SortableTree })))
const FavoriteNotes = lazy(() => import('@components/Sidebar/FavoriteNotes'))
const NoteGroups = lazy(() => import('@components/Sidebar/NoteGroups'))
const TrashNotes = lazy(() => import('@components/Sidebar/TrashNotes'))
const RecentlyModifiedNotes = lazy(() => import('@components/Sidebar/RecentlyModifiedNotes'))
const ShareManagerButton = lazy(() => import('@components/Share/ShareManagerButton'))
import { QuotaDisplay, QuotaExceededModal } from '@components/Subscription'
import Header from '@components/Header/Header'
import NotificationContainer from '@components/ui/NotificationContainer'
const BlockReferencePanel = lazy(() => import('@components/Panels/BlockReferencePanel'))
const KnowledgeGraphPanel = lazy(() => import('@components/Panels/KnowledgeGraphPanel'))
const TocOutlineView = lazy(() => import('@components/Panels/TocOutlineView'))
const RightEditorContainer = lazy(() => import('@components/RightEditor/RightEditorContainer'))
const LeftEditorContainer = lazy(() => import('@components/LeftEditor/LeftEditorContainer'))
import ResizableSplitter from '@components/ui/ResizableSplitter'
import { EditorRegistryProvider } from '@contexts/EditorRegistryContext'

import { RootState, useAppDispatch } from '@store'
import { closeNoteEditor as closeStandardNoteEditor } from '@store/slices/standardNoteEditorSlice'
import { closeNoteEditor as closeSujiNoteEditor } from '@store/slices/sujiNoteEditorSlice'
import { updateNote, updateNoteCollapse, createNote, moveNoteToTrash, updateNoteHierarchy, lockNote, unlockNote, fetchNotes, setFilters, batchDeleteNotesThunk, batchMoveNotes } from '@store/slices/notesSlice'
import { lockGlobally, selectIsGloballyUnlocked } from '@store/slices/lockSessionSlice'
import { openNoteInRightEditor, setRightEditorWidth, setRightEditorCurrentNote, setRightEditorFullscreen, closeRightEditor, closeAllNotesInRightEditor } from '@store/slices/rightEditorSlice'
import { openLeftEditor, setLeftEditorWidth } from '@store/slices/leftEditorSlice'
import { openNoteInCRDTMultiNoteEditor, closeAllNotesInCRDTMultiNoteEditor, setCRDTMultiNoteEditorCurrentNote } from '@store/slices/crdtMultiNoteEditorSlice'
import { switchMode } from '@store/slices/modeSlice'
import { toggleTocOutline, setSidebarSearchOpen, setSidebarOpen, setSidebarFullscreen } from '@store/slices/uiSlice'
import { useEditorContext } from '@contexts/EditorContext'
import { BatchDeleteProvider, useBatchDelete } from '@contexts/BatchDeleteContext'
import { BatchDeleteModal } from '@components/BatchDelete/BatchDeleteModal'
import { LockPasswordInitModal, LockNoteModal, UnlockNoteModal, LockSessionManager, LockStatusIndicator } from '@components/LockNote'
const SearchPanel = lazy(() => import('@components/Search/SearchPanel'))
import { unifiedApi } from '../../api/unifiedApi'
import { useSyncCollectionToRedux } from '../../hooks/useSyncCollectionToRedux'
import { useSyncNoteGroupsToRedux } from '../../hooks/useSyncNoteGroupsToRedux'
import { isTauriMobilePlatform, getSidebarWidth } from '../../utils/mobile'
import { SUJI_MODE_CONFIG } from '../../config/sujiMode.config'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { useMobile, useKeyboardState } from '../../hooks/useMobile'
import { MobileUserMenu } from '../../auth'
import { useSwipeGesture } from '../../hooks/useSwipeGesture'
import { useEditorSwipe } from '../../hooks/useEditorSwipe'
import { hapticLight, hapticMedium } from '../../utils/haptic'
const MobileKeyboardToolbar = lazy(() => import('@components/Editor/MobileKeyboardToolbar'))

// ★ 2026-10-31：侧边栏 VIP 升级购买入口（会员框 QuotaDisplay，含套餐状态/升级
// 按钮跳 /plans 支付页）暂时关闭不显示。恢复入口时改回 true 即可，组件与
// /plans 路由、QuotaExceededModal 均未删除。
const SHOW_SIDEBAR_VIP_ENTRY = false

const Layout: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { sidebarOpen, sidebarFullscreen, blockReferencePanelOpen, tocOutlineOpen, graphPanelOpen } = useSelector((state: RootState) => state.ui)
  const { notes, currentNote, filters } = useSelector((state: RootState) => state.notes)
  const { current: currentMode } = useSelector((state: RootState) => state.mode)
  const isGloballyUnlocked = useSelector(selectIsGloballyUnlocked)
  const { activeEditor } = useEditorContext()
  const { startBatchDelete, startBatchDeleteMany } = useBatchDelete()

  const mobileInfo = useMobile()
  const isMobile = mobileInfo.isTauriMobile
  // 键盘弹出高度：侧边栏滚动到底时，用键盘高度动态补偿底部留白，
  // 确保会员框（QuotaDisplay）能滚动到键盘上方不被遮挡
  const { isOpen: keyboardOpen, keyboardHeight } = useKeyboardState()
  // 移动端：全屏时 100vw，半屏时 50vw；桌面端固定 320px
  const sidebarWidth = isMobile
    ? (sidebarFullscreen ? '100vw' : '50vw')
    : getSidebarWidth()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const editorAreaRef = useRef<HTMLDivElement>(null)
  // 侧边栏滚动容器 ref（上滑收键盘需要监听该滚动的触摸）
  const sidebarScrollRef = useRef<HTMLDivElement>(null)
  const isSujiMode = location.pathname.startsWith('/suji')
  const [modeAnimClass, setModeAnimClass] = useState('')

  // 模式切换淡入动画（仅 suji↔crdt 切换时触发，不响应笔记内导航）
  const prevModeRef = useRef(isSujiMode)
  useEffect(() => {
    if (prevModeRef.current !== isSujiMode) {
      prevModeRef.current = isSujiMode
      setModeAnimClass('mode-fade-in')
      const timer = setTimeout(() => setModeAnimClass(''), 300)
      return () => clearTimeout(timer)
    }
  }, [isSujiMode])

  // 移动端侧边栏水平滑动（右滑：半屏→全屏；左滑：全屏→半屏/半屏→关闭）
  // 采用项目已验证的 useSwipeGesture 双实例模式（同 CRDTMultiNoteEditorContainer 标签切换）
  // 搜索面板区（SearchPanel data-search-panel / 结果列表 data-search-results）的手势由 SearchPanel 自行处理
  // （右滑→全屏搜索、左滑→收起侧边栏），此区域让位不拦截。
  const isInsideSearchResults = (startTarget: Element | null) =>
    !!startTarget?.closest('[data-search-results], [data-search-panel]')
  useSwipeGesture({
    ref: sidebarRef,
    direction: 'right',
    threshold: 60,
    onSwipe: (startTarget) => {
      if (isInsideSearchResults(startTarget)) return
      if (!sidebarFullscreen) {
        dispatch(setSidebarFullscreen(true))
        hapticLight()
      }
    },
    enabled: isMobile && sidebarOpen && !sidebarFullscreen,
  })
  useSwipeGesture({
    ref: sidebarRef,
    direction: 'left',
    threshold: 60,
    onSwipe: (startTarget) => {
      if (isInsideSearchResults(startTarget)) return
      if (sidebarFullscreen) {
        dispatch(setSidebarFullscreen(false))
      } else {
        dispatch(setSidebarOpen(false))
        dispatch(setSidebarFullscreen(false))
      }
      hapticLight()
    },
    enabled: isMobile && sidebarOpen,
  })

  // 速记页左滑切 CRDT 已由模式轨道手势接管（EditorModeHost ModeTrack + 容器 useTrackSwipe，
  // 跟手拖动 + 松手判定），此处仅保留侧边栏手势职责（onSwitchToCrdt 不再传入）
  useEditorSwipe({
    ref: editorAreaRef,
    enabled: isMobile && isSujiMode && !sidebarFullscreen,
    sidebarOpen,
    sidebarFullscreen,
    onOpenSidebar: () => {
      dispatch(setSidebarOpen(true))
      dispatch(setSidebarFullscreen(false))
    },
    onExpandSidebar: () => {
      dispatch(setSidebarFullscreen(true))
    },
    onCloseSidebar: () => {
      dispatch(setSidebarOpen(false))
      dispatch(setSidebarFullscreen(false))
    },
  })

  // ★ 移动端：上滑侧边栏即收起键盘（侧边栏打开时搜索框自动聚焦弹键盘；
  // 用户上滑浏览笔记列表应立即收起，列表内容不被键盘遮挡）
  useEffect(() => {
    if (!sidebarOpen || !isMobile) return
    const el = sidebarScrollRef.current
    if (!el) return

    let startY = 0
    let dismissed = false
    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0]?.clientY ?? 0
      dismissed = false
    }
    const onTouchMove = (e: TouchEvent) => {
      if (dismissed) return
      const dy = (e.touches[0]?.clientY ?? startY) - startY
      if (dy < -10) {
        // 手指向上滑动 → 立即收起键盘并失焦
        dismissed = true
        try {
          const bridge = (window as any).LingShiKeyboard
          if (bridge && typeof bridge.hideKeyboard === 'function') {
            bridge.hideKeyboard()
          }
        } catch { /* 静默：桌面/浏览器无桥 */ }
        const active = document.activeElement as HTMLElement | null
        if (active && (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          active.blur()
        }
      }
    }
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
    }
  }, [sidebarOpen, isMobile])

  // ★ 全部笔记页（/notes）已并入模式轨道（移动端 /notes 渲染 EditorModeHost，
  // CRDT 槽空态渲染全部笔记列表）：右滑跟手带出速记、左滑橡皮筋——由容器内
  // useTrackSwipe + ModeTrack 处理，不再有独立的页面级滑动手势
  // （原"左滑 hash 进速记 + 右滑拉右参考编辑器"手势已移除）

  // ============================================================
  // 移动端全局左边缘滑动拦截（从左屏幕边缘向右滑 → 拉出侧边栏半屏）
  // 替代默认的"后退/退出"系统手势行为
  // ============================================================
  const edgeSwipeStateRef = useRef({
    sidebarOpen,
    sidebarFullscreen,
    // 最近一次成功触发的时间戳（用于 popstate 抵消 back）
    lastTriggeredAt: 0,
  })
  useEffect(() => {
    edgeSwipeStateRef.current.sidebarOpen = sidebarOpen
    edgeSwipeStateRef.current.sidebarFullscreen = sidebarFullscreen
  }, [sidebarOpen, sidebarFullscreen])

  useEffect(() => {
    const el = editorAreaRef.current
    if (!el || !isMobile) return

    const EDGE_MARGIN = 20
    const THRESHOLD = 60
    const DIRECTION_LOCK_RATIO = 1.5
    // 成功触发后 popstate 抵消窗口（ms）：Android 系统手势从触发到 popstate 的间隔通常 <300ms
    const POPSTATE_WINDOW = 500

    const state = {
      startX: 0,
      startY: 0,
      isTracking: false,
      isTriggered: false,
      directionLocked: false,
      pointerId: -1,
    }

    const cleanup = () => {
      state.isTracking = false
      state.isTriggered = false
      state.directionLocked = false
      state.pointerId = -1
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('pointercancel', handlePointerCancel)
    }

    const handleEdgeSwipe = (deltaX: number) => {
      if (deltaX > THRESHOLD) {
        const { sidebarOpen: so } = edgeSwipeStateRef.current
        if (!so) {
          // 仅侧边栏未打开时才操作：已打开则任何情况下不操作
          dispatch(setSidebarOpen(true))
          dispatch(setSidebarFullscreen(false))
          hapticLight()
          edgeSwipeStateRef.current.lastTriggeredAt = Date.now()
          state.isTriggered = true
        } else {
          // 已打开 → 不做任何操作（含不后退、不全屏、不关闭）
          state.isTriggered = true
        }
      }
    }

    const handlePointerMove = (e: PointerEvent) => {
      if (!state.isTracking || state.isTriggered) return
      if (e.pointerId !== state.pointerId) return
      const deltaX = e.clientX - state.startX
      const deltaY = e.clientY - state.startY
      if (!state.directionLocked) {
        if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return
        if (Math.abs(deltaY) > Math.abs(deltaX) * DIRECTION_LOCK_RATIO) {
          cleanup()
          return
        }
        state.directionLocked = true
      }
      // 尝试尽早阻止系统级手势
      if (deltaX > 10) {
        try { e.preventDefault() } catch (_) { /* ignore passive */ }
      }
      if (Math.abs(deltaX) >= THRESHOLD) {
        handleEdgeSwipe(deltaX)
        cleanup()
      }
    }

    const handlePointerUp = (e: PointerEvent) => {
      if (!state.isTracking || state.isTriggered) { cleanup(); return }
      if (e.pointerId !== state.pointerId) { cleanup(); return }
      const deltaX = e.clientX - state.startX
      const deltaY = e.clientY - state.startY
      if (state.directionLocked || Math.abs(deltaY) < Math.abs(deltaX) * DIRECTION_LOCK_RATIO) {
        if (Math.abs(deltaX) >= THRESHOLD) {
          handleEdgeSwipe(deltaX)
        }
      }
      cleanup()
    }

    const handlePointerCancel = () => cleanup()

    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return
      if (state.isTracking) return
      // 关键：只在左边缘 EDGE_MARGIN (20px) 区域内开始的 pointerdown 追踪
      if (e.clientX > EDGE_MARGIN) return
      state.startX = e.clientX
      state.startY = e.clientY
      state.isTracking = true
      state.isTriggered = false
      state.directionLocked = false
      state.pointerId = e.pointerId
      try { e.stopImmediatePropagation() } catch (_) { /* ignore */ }
      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerUp)
      document.addEventListener('pointercancel', handlePointerCancel)
    }

    // popstate 兜底：抵消 Android 系统边缘后退触发的 history.back
    const handlePopstate = () => {
      const now = Date.now()
      const last = edgeSwipeStateRef.current.lastTriggeredAt
      if (now - last <= POPSTATE_WINDOW) {
        // 我们刚刚成功触发了侧边栏打开，重新压入当前 URL 抵消 back
        window.history.pushState(null, '', window.location.href)
      }
    }

    el.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('popstate', handlePopstate)

    return () => {
      el.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('popstate', handlePopstate)
      cleanup()
    }
  }, [isMobile, dispatch])

  // 文档树用笔记：按 order 排序，与全部笔记的 sortConfig 解耦
  const treeNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      const orderDiff = (a.order || 0) - (b.order || 0);
      if (orderDiff !== 0) return orderDiff;
      return a.id.localeCompare(b.id);
    })
  }, [notes])

  const truncateTitle = (title: string) => {
    if (!title) return '...'
    if (title.length > 2) return title.slice(0, 2) + '...'
    return title
  }

  const handleRemoveParentFilter = useCallback(() => {
    dispatch(setFilters({ parentId: null, parentTitle: null }))
  }, [dispatch])

  const handleRemoveSearchFilter = useCallback(() => {
    dispatch(setFilters({ search: '' }))
  }, [dispatch])

  // Electric + TanStack DB + Redux 最佳实践：
  // 使用 useSyncCollectionToRedux hook 监听 Collection 变更
  // 当 Electric 推送变更时，自动同步到 Redux store
  useSyncCollectionToRedux()
  useSyncNoteGroupsToRedux()

  // 同步 currentMode 与当前路由，确保 handleNoteClick/handleAddNote 等使用正确的模式
  useEffect(() => {
    const path = location.pathname
    let targetMode: 'suji' | 'crdt' | null = null
    if (path.startsWith('/suji')) {
      targetMode = 'suji'
    } else if (path.startsWith('/crdt') || path.startsWith('/notes')) {
      targetMode = 'crdt'
    }
    if (targetMode && currentMode !== targetMode) {
      dispatch(switchMode({ mode: targetMode }))
      // ★ 切到编辑模式：收起软键盘（速记模式启动聚焦弹出的键盘，编辑模式不需要）
      // 与 SujiCRDTMultiNoteEditorPage 的启动聚焦对称：聚焦只在速记模式，
      // 离开速记模式时收起键盘，避免编辑模式下键盘残留遮挡。
      if (targetMode === 'crdt') {
        try {
          const bridge = (window as any).LingShiKeyboard
          if (bridge && typeof bridge.hideKeyboard === 'function') {
            bridge.hideKeyboard()
          }
        } catch (e) {
          // 静默：桌面端/无桥环境
        }
        // 前端失焦：确保 useKeyboardState 的 focusin 通道判定键盘关闭
        const active = document.activeElement as HTMLElement | null
        if (active && (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          active.blur()
        }
      }
    }
  }, [location.pathname, currentMode, dispatch])

  // ★ 进入速记模式时清除全部笔记页筛选（独立监听，无条件执行）：
  // 速记模式从 Collection 全量读取数据。若保留父笔记/搜索/标签筛选，
  // Redux notes 会被过滤，导致速记库子笔记被误判为"已删除"而关闭，
  // 出现"所有笔记都已关闭"空状态。清除筛选后重新 fetchNotes 恢复全量。
  const notesFilters = useSelector((state: RootState) => state.notes.filters)
  useEffect(() => {
    if (!location.pathname.startsWith('/suji')) return
    if (notesFilters.search || notesFilters.tags.length > 0 || notesFilters.isPinned || notesFilters.parentId) {
      console.log('[Layout] 进入速记模式，清除全部笔记筛选')
      dispatch(setFilters({ search: '', tags: [], isPinned: false, parentId: null, parentTitle: null }))
      dispatch(fetchNotes(undefined) as any)
    }
  }, [location.pathname, notesFilters, dispatch])

  // 密码初始化弹窗状态
  const [lockInitModalOpen, setLockInitModalOpen] = useState(false)
  const [lockConfirmModalOpen, setLockConfirmModalOpen] = useState(false)
  const [unlockModalOpen, setUnlockModalOpen] = useState(false)
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  const [selectedNoteTitle, setSelectedNoteTitle] = useState('')
  const [sidebarSelectedId, setSidebarSelectedId] = useState<UniqueIdentifier | null>(null)
  // ★ 选中笔记下创建子笔记时的一次性展开请求（nonce 保证同一父笔记可重复触发）
  const [expandRequest, setExpandRequest] = useState<{ id: string; nonce: number } | null>(null)
  
  // 获取右侧编辑栏状态
  const {
    open: rightEditorOpen,
    width: rightEditorWidth,
    fullscreen: rightEditorFullscreen
  } = useSelector((state: RootState) => state.rightEditor)

  // 移动端：打开右参考编辑器时自动关闭侧边栏，避免三栏显示
  useEffect(() => {
    if (isMobile && rightEditorOpen && sidebarOpen) {
      dispatch(setSidebarOpen(false))
    }
  }, [isMobile, rightEditorOpen, sidebarOpen, dispatch])

  // 获取左侧编辑栏状态
  const {
    open: leftEditorOpen,
    width: leftEditorWidth
  } = useSelector((state: RootState) => state.leftEditor)

  // 处理右侧分割器拖动
  const handleRightSplitterDrag = useCallback((newWidth: number) => {
    dispatch(setRightEditorWidth(newWidth))
  }, [dispatch])

  // 处理左侧分割器拖动
  const handleLeftSplitterDrag = useCallback((newWidth: number) => {
    dispatch(setLeftEditorWidth(newWidth))
  }, [dispatch])
  
  // 递归转换treeItems为更新数据
  const convertTreeItemsToUpdates = (treeItems: any[], parentId?: UniqueIdentifier): Array<{ id: string; parentId?: string; order: number }> => {
    const updates: Array<{ id: string; parentId?: string; order: number }> = []
    
    treeItems.forEach((item, index) => {
      updates.push({
        id: item.id.toString(),
        parentId: parentId ? parentId.toString() : null,
        order: index
      })
      
      if (item.children && item.children.length > 0) {
        updates.push(...convertTreeItemsToUpdates(item.children, item.id))
      }
    })
    
    return updates
  }
  
  // 处理笔记拖拽结束
  const handleDragEnd = (treeItems: any[]) => {
    // 将treeItems转换为笔记更新数据
    const updates = convertTreeItemsToUpdates(treeItems)
    
    // 使用专用的批量层级更新action，避免逐个updateNote触发多次Electric同步
    dispatch(updateNoteHierarchy(updates))
  }
  
  // 处理笔记折叠/展开
  const handleCollapse = (id: UniqueIdentifier, collapsed: boolean) => {
    dispatch(updateNoteCollapse({ id: id.toString(), collapsed }))
  }

  // 处理添加顶级笔记（有选中项时在选中项下级创建）
  const handleAddNote = () => {
    if (sidebarSelectedId) {
      handleAddChildNote(sidebarSelectedId)
      return
    }
    // 创建新的顶级笔记，创建后自动打开
    dispatch(createNote({ title: '新笔记', content: '', isFolder: false }))
      .unwrap()
      .then((newNote: any) => {
        // 直接使用 newNote 导航，因为 notes 闭包可能还未更新
        // 统一在 CRDT 主编辑器中打开（与 handleNoteClick 保持一致）
        navigate(`/crdt/notes/${newNote.id}`)
      })
  }

  // 处理添加电子表格笔记（有选中项时同样挂到选中项下级，与普通笔记一致）
  const handleAddSheetNote = () => {
    // ★ 与普通子笔记一致：挂到选中项下级时先展开选中项，让新表格笔记立即可见
    if (sidebarSelectedId) {
      setExpandRequest({ id: sidebarSelectedId.toString(), nonce: Date.now() })
      dispatch(updateNoteCollapse({ id: sidebarSelectedId.toString(), collapsed: false }))
    }
    dispatch(createNote({ title: '未命名表格', content: createEmptySheetContent(), isFolder: false, icon: 'spreadsheet' }))
      .unwrap()
      .then((newNote: any) => {
        if (sidebarSelectedId) {
          dispatch(updateNote({ id: newNote.id, parentId: sidebarSelectedId.toString() }))
        }
        navigate(`/crdt/notes/${newNote.id}`)
      })
  }

  // 处理添加子笔记
  const handleAddChildNote = (parentId: UniqueIdentifier) => {
    // ★ 创建子笔记前先展开父笔记（含祖先）：父笔记折叠时新建的子笔记不可见，
    // 展开让用户立即看到新笔记落入的位置；同时持久化展开状态（刷新后保持）
    setExpandRequest({ id: parentId.toString(), nonce: Date.now() })
    dispatch(updateNoteCollapse({ id: parentId.toString(), collapsed: false }))
    // 创建新的子笔记，创建后自动打开
    dispatch(createNote({ title: '新子笔记', content: '', isFolder: false }))
      .unwrap()
      .then((newNote: any) => {
        // 将新笔记设置为指定父笔记的子笔记
        dispatch(updateNote({ id: newNote.id, parentId: parentId.toString() }))
        // 直接使用 newNote 导航，因为 notes 闭包可能还未更新
        // 统一在 CRDT 主编辑器中打开（与 handleNoteClick 保持一致）
        navigate(`/crdt/notes/${newNote.id}`)
      })
  }

  // 处理添加顶级文件夹（有选中项时在选中项下级创建）
  const handleAddFolder = () => {
    if (sidebarSelectedId) {
      handleAddChildFolder(sidebarSelectedId)
      return
    }
    // 创建新的顶级文件夹
    dispatch(createNote({ title: '新文件夹', content: '', isFolder: true }))
  }

  // 处理添加子文件夹
  const handleAddChildFolder = (parentId: UniqueIdentifier) => {
    // ★ 同子笔记：展开父级，让新建子文件夹立即可见
    setExpandRequest({ id: parentId.toString(), nonce: Date.now() })
    dispatch(updateNoteCollapse({ id: parentId.toString(), collapsed: false }))
    // 创建新的子文件夹
    dispatch(createNote({ title: '新子文件夹', content: '', isFolder: true }))
      .unwrap()
      .then((newFolder: any) => {
        // 将新文件夹设置为指定父笔记/文件夹的子项
        dispatch(updateNote({ id: newFolder.id, parentId: parentId.toString() }))
      })
  }

  // ★ 速记库防误删确认弹窗：速记库是速记模式的核心容器，删除需强确认
  // （否则误删后触发自动重建循环，且回收站会反复出现"速记库"笔记）
  const [sujiDeleteConfirm, setSujiDeleteConfirm] = useState<{ noteId: string; childCount: number } | null>(null)
  const [sujiDeleteLoading, setSujiDeleteLoading] = useState(false)

  // 处理删除笔记 - 移入回收站
  const handleRemoveNote = async (id: UniqueIdentifier) => {
    try {
      const noteId = id.toString()

      // ★ 速记库拦截：删除前强确认（应用内 ConfirmDialog，Tauri WebView 禁用 window.confirm）
      const note = notes.find((n: any) => n.id === noteId)
      if (
        note &&
        note.title === SUJI_MODE_CONFIG.PARENT_NOTE_TITLE &&
        !note.parentId &&
        String(note.isDeleted) !== 'true'
      ) {
        const childCount = notes.filter((n: any) => n.parentId === noteId).length
        setSujiDeleteConfirm({ noteId, childCount })
        return
      }

      // 检查笔记是否有子笔记（通过查找是否有笔记的 parentId 等于当前笔记 id）
      const hasChildren = notes.some((n: any) => n.parentId === noteId)

      if (hasChildren) {
        startBatchDelete(noteId, notes)
      } else {
        await dispatch(moveNoteToTrash(noteId) as any)
        if (localStorage.getItem('last_edited_note_id') === noteId) {
          localStorage.removeItem('last_edited_note_id')
        }
      }
    } catch (error) {
      console.error('移入回收站失败:', error)
      alert('移入回收站失败，请重试')
    }
  }

  // 速记库删除确认执行（沿用原级联删除逻辑：有子笔记走批量删除，无子笔记直接入回收站）
  const handleConfirmSujiDelete = async () => {
    if (!sujiDeleteConfirm) return
    const { noteId } = sujiDeleteConfirm
    setSujiDeleteLoading(true)
    try {
      const hasChildren = notes.some((n: any) => n.parentId === noteId)
      if (hasChildren) {
        setSujiDeleteConfirm(null)
        startBatchDelete(noteId, notes)
      } else {
        await dispatch(moveNoteToTrash(noteId) as any)
        if (localStorage.getItem('last_edited_note_id') === noteId) {
          localStorage.removeItem('last_edited_note_id')
        }
        setSujiDeleteConfirm(null)
      }
    } catch (error) {
      console.error('删除速记库失败:', error)
    } finally {
      setSujiDeleteLoading(false)
    }
  }

  // 处理笔记点击 - 使用多笔记编辑器打开笔记
  const handleNoteClick = (noteId: UniqueIdentifier, title?: string) => {
    // 找到对应的笔记
    const note = notes.find((n: any) => n.id === noteId.toString())
    if (note) {
      // 仅在未全局解锁时，点击上锁笔记才触发锁定锁遮罩
      // 如果已全局解锁，直接打开笔记，不重新上锁
      if (note.isLocked && !isGloballyUnlocked) {
        dispatch(lockGlobally())
      }

      // 统一在 CRDT 主编辑器中打开笔记（无论当前是 suji 还是 crdt 模式）
      // 速记模式是速记库的批量视图，不应通过侧边栏点击进入；
      // 标准模式为死代码，不走其分支
      dispatch(openNoteInCRDTMultiNoteEditor(note.id))
      navigate(`/crdt/notes/${noteId.toString()}`)
    }
  }

  // 处理在右侧编辑栏打开笔记
  const handleOpenInRightEditor = (noteId: UniqueIdentifier) => {
    // 移动端：先关闭左侧边栏（含重置全屏状态），再从右边拉出右参考编辑器
    if (isMobile) {
      dispatch(setSidebarOpen(false))
      dispatch(setSidebarFullscreen(false))
    }
    dispatch(openNoteInRightEditor(noteId.toString()))
  }

  // 处理切换收藏状态
  const handleToggleFavorite = (noteId: string) => {
    const note = notes.find((n: any) => n.id === noteId);
    if (note) {
      dispatch(updateNote({ id: noteId, isPinned: !note.isPinned }));
    }
  }

  // 处理笔记上锁
  const handleLockNote = async (noteId: UniqueIdentifier) => {
    const note = notes.find((n: any) => n.id === noteId)
    if (!note) return
    
    setSelectedNoteId(noteId.toString())
    setSelectedNoteTitle(note.title || 'Untitled Note')
    
    try {
      // 检查是否已设置锁密码（本地镜像 + 云端兜底，跨设备一致）
      const hasPwd = await unifiedApi.hasLockPassword()

      if (hasPwd) {
        // 已设置密码（任意设备），直接上锁
        dispatch(lockNote(noteId.toString()) as any)
      } else {
        // 未设置过密码，显示密码初始化弹窗
        setLockInitModalOpen(true)
      }
    } catch (err) {
      console.error('检查上锁状态失败:', err)
    }
  }

  // 处理笔记取消上锁
  const handleUnlockNote = (noteId: UniqueIdentifier) => {
    const note = notes.find((n: any) => n.id === noteId)
    if (!note) return
    
    setSelectedNoteId(noteId.toString())
    setSelectedNoteTitle(note.title || 'Untitled Note')
    setUnlockModalOpen(true)
  }

  // 处理恢复上锁状态（全局已解锁时，右键菜单恢复锁定所有笔记）
  const handleReLockNote = () => {
    dispatch(lockGlobally())
  }
  
  // 处理取消上锁成功
  const handleUnlockSuccess = () => {
    // 取消上锁成功，刷新笔记列表
    dispatch(fetchNotes(undefined) as any)
  }
  
  // 处理密码初始化成功
  const handleLockInitSuccess = () => {
    // 密码已设置且笔记已上锁，刷新笔记列表
    // 不需要额外操作，因为 LockPasswordInitModal 已经调用了 lockNote mutation
  }

  // 处理删除前打开笔记
  const handleOpenNoteBeforeDelete = (noteId: string) => {
    const note = notes.find((n: any) => n.id === noteId);
    if (note) {
      // 统一在 CRDT 主编辑器中打开（与 handleNoteClick 保持一致）
      navigate(`/crdt/notes/${noteId}`);
    }
  }

  // 处理批量删除中的单个笔记删除
  const handleBatchDeleteNote = useCallback(async (noteId: string) => {
    await dispatch(moveNoteToTrash(noteId) as any)
    dispatch(closeStandardNoteEditor(noteId))
    dispatch(closeSujiNoteEditor(noteId))
    if (localStorage.getItem('last_edited_note_id') === noteId) {
      localStorage.removeItem('last_edited_note_id')
    }
  }, [dispatch])

  // 处理批量删除中的移到根目录
  const handleMoveToRoot = useCallback(async (noteId: string) => {
    // 将笔记的 parentId 设为 null，移动到根目录
    // 使用 updateNoteHierarchy 专门处理层级关系变更
    await dispatch(updateNoteHierarchy([{ id: noteId, parentId: null, order: 0 }]))
  }, [dispatch])

  // 处理多选批量删除
  const handleBatchDelete = useCallback(async (ids: string[]) => {
    // 检查是否有笔记含子笔记
    const hasAnyChildren = ids.some(id => notes.some((n: any) => n.parentId === id))
    if (hasAnyChildren) {
      // 有子笔记，使用 BatchDeleteContext 的流程（单次收集所有根及其后代，避免 reducer 覆盖）
      startBatchDeleteMany(ids, notes)
    } else {
      // 无子笔记，逐个移入回收站（软删除，与单个笔记删除行为一致）
      for (const id of ids) {
        await dispatch(moveNoteToTrash(id) as any)
      }
    }
  }, [dispatch, notes, startBatchDeleteMany])

  // 处理多选批量移动
  const handleBatchMove = useCallback(async (params: { noteIds: string[]; targetParentId: string | null }) => {
    await dispatch(batchMoveNotes(params) as any)
  }, [dispatch])

  // 处理多选批量复制（递归复制笔记/文件夹及其子笔记）
  const handleBatchCopy = useCallback(async (params: { noteIds: string[]; targetParentId: string | null }) => {
    const { noteIds, targetParentId } = params
    // 递归复制笔记及其子笔记
    const copyNoteRecursive = async (sourceNoteId: string, newParentId: string | null, isTopLevel: boolean) => {
      const sourceNote = notes?.find(n => n.id === sourceNoteId)
      if (!sourceNote) return
      // 顶层复制的笔记名加 "副本 -" 前缀，子笔记保持原名
      const newTitle = isTopLevel ? `副本 - ${sourceNote.title}` : sourceNote.title
      const resultAction = await dispatch(createNote({
        title: newTitle,
        content: sourceNote.content || '',
        tags: sourceNote.tags,
        isFolder: sourceNote.isFolder,
        parentId: newParentId || undefined,
        icon: sourceNote.icon,
      }) as any)
      const newNote = (resultAction as any).payload as Note | undefined
      // 如果是文件夹，递归复制子笔记
      if (sourceNote.isFolder && newNote?.id) {
        const children = notes?.filter(n => n.parentId === sourceNoteId) || []
        for (const child of children) {
          await copyNoteRecursive(child.id, newNote.id, false)
        }
      }
    }
    for (const noteId of noteIds) {
      await copyNoteRecursive(noteId, targetParentId, true)
    }
  }, [dispatch, notes])

  // 键盘快捷键处理
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // ★ 可编辑区域守卫：在输入框/文本域/编辑器（contenteditable/ProseMirror）中
      // 反引号是高频字符（Markdown 代码块），不应被快捷键吞掉
      const target = event.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          target.isContentEditable ||
          target.closest('.ProseMirror') ||
          target.closest('[contenteditable="true"]')
        ) {
          return
        }
      }
      // Home键左边的键（通常是 ` 键或 ~ 键，取决于键盘布局）
      if (event.key === '`' || event.key === '~' || event.key === '¬' || event.key === '§') {
        event.preventDefault()
        dispatch(toggleTocOutline())
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [dispatch])

  return (
    <EditorRegistryProvider>
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`${sidebarOpen ? '' : 'w-0'} transition-all duration-300 flex-shrink-0 overflow-hidden bg-white dark:bg-gray-800 ${sidebarFullscreen ? '' : 'border-r border-gray-200 dark:border-gray-700'}`}
        style={sidebarOpen ? { width: sidebarWidth, touchAction: 'pan-y' } : undefined}
      >
        {/* 侧边栏内容仅在打开时渲染（组件已懒加载，本地 chunk 按需加载；关闭时卸载） */}
        {sidebarOpen && (
        <Suspense fallback={null}>
        {/* ★ 2026-10-30：横向避让拆分——半屏(50vw)时右缘是内容分线而非屏幕
            边缘，只加 safe-area-left（右侧避让会在滚动条与分线间产生间隙）；
            全屏(100vw)时右缘即屏幕右缘（横屏可能命中刘海/圆角），才加
            safe-area-right。纵向仍整列 safe-area-top（桌面/鸿蒙经平台分诊
            已归零，Android edge-to-edge 上报真实状态栏高度）。 */}
        <div className={`h-full flex flex-col safe-area-top safe-area-left ${sidebarFullscreen ? 'safe-area-right' : ''}`}>
          <div
            ref={sidebarScrollRef}
            className="flex-1 overflow-auto p-4"
            style={{
              touchAction: 'pan-y',
              // 键盘弹出时底部额外留白（keyboardHeight），使会员框可滚动到键盘上方
              paddingBottom: keyboardOpen ? keyboardHeight + 16 : undefined,
            }}
          >
          {/* 搜索面板 - 始终显示 */}
          <div className="mb-2">
            <SearchPanel
              variant="sidebar"
              onClose={() => dispatch(setSidebarSearchOpen(false))}
              headerRight={
                <button
                  onClick={() => {
                    dispatch(setSidebarOpen(false))
                    dispatch(setSidebarFullscreen(false))
                  }}
                  className="p-1.5 rounded-lg transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0"
                  title="关闭侧边栏"
                  type="button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a2 2 0 00-2 2v12a2 2 0 002 2h4m0-16h10a2 2 0 012 2v12a2 2 0 01-2 2H9m0-16v16" />
                  </svg>
                </button>
              }
            />
          </div>

          {/* 最近修改模块 */}
          <RecentlyModifiedNotes
            onNoteClick={handleNoteClick}
            onOpenInRightEditor={handleOpenInRightEditor}
          />

          {/* 笔记模块 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2
                className="text-lg font-semibold text-gray-800 dark:text-gray-200 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors inline-flex items-center gap-1.5"
                onClick={() => {
                  setSidebarSelectedId(null)
                  navigate('/notes')
                }}
                title="全部笔记"
              >
                笔记
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
                {filters.parentId && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 max-w-[64px]">
                    <span className="truncate">{truncateTitle(filters.parentTitle || filters.parentId)}</span>
                    <button onClick={handleRemoveParentFilter} className="hover:text-purple-900 dark:hover:text-purple-100 flex-shrink-0">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {filters.search && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 max-w-[80px]">
                    <span className="truncate">{truncateTitle(filters.search)}</span>
                    <button onClick={handleRemoveSearchFilter} className="hover:text-green-900 dark:hover:text-green-100 flex-shrink-0">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleAddNote}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
                  title="添加新笔记"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2zM14 2v5a1 1 0 0 0 1 1h5M9 15h6M12 18v-6" />
                  </svg>
                </button>
                <button
                  onClick={handleAddSheetNote}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
                  title="新建电子表格"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2zM14 2v5a1 1 0 0 0 1 1h5M8 13h2M14 13h2M8 17h2M14 17h2" />
                  </svg>
                </button>
              </div>
            </div>
            <SortableTree 
              collapsible 
              removable 
              indicator 
              notes={treeNotes} 
              isGloballyUnlocked={isGloballyUnlocked}
              onDragEnd={handleDragEnd} 
              onCollapse={handleCollapse} 
              onAddChildNote={handleAddChildNote} 
              onAddChildFolder={handleAddChildFolder} 
              onRemoveNote={handleRemoveNote} 
              onNoteClick={handleNoteClick} 
              onOpenInRightEditor={handleOpenInRightEditor}
              onToggleFavorite={handleToggleFavorite}
              onOpenNoteBeforeDelete={handleOpenNoteBeforeDelete}
              onLockNote={handleLockNote}
              onUnlockNote={handleUnlockNote}
              onReLockNote={handleReLockNote}
              onBatchDelete={handleBatchDelete}
              onBatchMove={handleBatchMove}
              onBatchCopy={handleBatchCopy}
              expandNoteId={filters.parentId}
              expandRequest={expandRequest}
              onSelectedChange={setSidebarSelectedId}
              externalSelectedId={sidebarSelectedId}
            />
          </div>
          
          {/* 收藏文档模块 */}
          <FavoriteNotes
            onNoteClick={handleNoteClick}
            onToggleFavorite={handleToggleFavorite}
            onOpenInRightEditor={handleOpenInRightEditor}
          />

          {/* 笔记组模块 */}
          <NoteGroups
            onGroupClick={(group) => {
              if (group.source === 'rightEditor') {
                // 先关闭右编辑器（保存当前标签到 lastOpenNoteIds），再清空标签列表，最后打开笔记组的笔记
                dispatch(closeRightEditor())
                dispatch(closeAllNotesInRightEditor())
                group.noteIds.forEach((noteId: string) => {
                  dispatch(openNoteInRightEditor(noteId))
                })
                if (group.noteIds.length > 0) {
                  dispatch(setRightEditorCurrentNote(group.noteIds[0]))
                }
              } else {
                // 关闭主编辑区当前所有笔记，打开笔记组的笔记
                dispatch(closeAllNotesInCRDTMultiNoteEditor())
                group.noteIds.forEach((noteId: string) => {
                  dispatch(openNoteInCRDTMultiNoteEditor(noteId))
                })
                if (group.noteIds.length > 0) {
                  dispatch(setCRDTMultiNoteEditorCurrentNote(group.noteIds[0]))
                  navigate(`/crdt/notes/${group.noteIds[0]}`)
                }
              }
            }}
          />

          {/* 回收站模块 */}
          <TrashNotes
            onNoteClick={handleNoteClick}
          />

          {/* 分享管理入口 */}
          <ShareManagerButton />

          {/* 配额显示（VIP 升级购买入口暂时关闭，见文件顶部 SHOW_SIDEBAR_VIP_ENTRY） */}
          {SHOW_SIDEBAR_VIP_ENTRY && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <QuotaDisplay />
            </div>
          )}

          {/* 用户菜单（头像+用户名）——侧边栏底部：点击弹出用户菜单（我的帐号/设置/退出等）。
              移动端集成到滚动流；桌面端同样显示在侧边栏底部（与移动端一致） */}
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <MobileUserMenu
              onCloseSidebar={() => {
                dispatch(setSidebarOpen(false))
                dispatch(setSidebarFullscreen(false))
              }}
            />
          </div>

          {/* 底部留白 + 灵拾笔记理念（键盘弹出时会员框可滚动到键盘上方，留白避免遮挡） */}
          <div className="pt-10 safe-area-bottom">
            <div className="px-2 text-center space-y-2">
              <div className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">灵拾笔记</span>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                极力探索 只为记录整理更快一秒！
              </p>
            </div>
          </div>
          </div>
        </div>
        </Suspense>
        )}
      </div>
      
      {/* 目录大纲视图 - 移动端不显示 */}
      {!isMobile && (
        <div className={`${tocOutlineOpen ? 'w-72' : 'w-0'} transition-all duration-300 flex-shrink-0 overflow-hidden border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800`}>
          {tocOutlineOpen && <Suspense fallback={null}><TocOutlineView editor={activeEditor} /></Suspense>}
        </div>
      )}

      {/* 左侧编辑栏 - 移动端不显示 */}
      {!isMobile && (
        <div
          className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 flex-shrink-0`}
          style={{
            width: leftEditorOpen ? `${leftEditorWidth}px` : '0px',
            flex: `0 0 ${leftEditorOpen ? leftEditorWidth : 0}px`
          }}
        >
          {leftEditorOpen && <Suspense fallback={null}><LeftEditorContainer /></Suspense>}
        </div>
      )}

      {/* 可拖动的分割器 - 仅在桌面端打开时显示 */}
      {!isMobile && leftEditorOpen && (
        <ResizableSplitter
          onDrag={handleLeftSplitterDrag}
          initialWidth={leftEditorWidth}
          minWidth={300}
          maxWidth={800}
          isLeftSplitter={true}
        />
      )}
      
      {/* Main content area with split layout */}
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        
        {/* 内容区域 - 支持与右侧编辑栏并列分割 */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* 主编辑器区域 */}
          <div
            ref={editorAreaRef}
            className={`flex-1 overflow-auto bg-white dark:bg-gray-800 ${sidebarFullscreen ? 'opacity-0 pointer-events-none' : 'opacity-100'} ${modeAnimClass}`}
            style={{
              marginRight: rightEditorOpen
                ? (isMobile ? '50%' : `${rightEditorWidth}px`)
                : '0px',
              transition: 'margin-right 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <Outlet />
          </div>

          {/* 右侧编辑栏 - 桌面端 */}
          {!isMobile && (
            <div
              className={`absolute right-0 top-0 bottom-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300`}
              style={{
                width: rightEditorOpen ? `${rightEditorWidth}px` : '0px'
              }}
            >
              {rightEditorOpen && (
                <>
                  {/* 可拖动的分割器 */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 z-10"
                    onMouseDown={(e) => {
                      const startX = e.clientX
                      const startWidth = rightEditorWidth

                      const handleMouseMove = (e: MouseEvent) => {
                        const delta = startX - e.clientX
                        const newWidth = Math.max(300, Math.min(800, startWidth + delta))
                        handleRightSplitterDrag(newWidth)
                      }

                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove)
                        document.removeEventListener('mouseup', handleMouseUp)
                      }

                      document.addEventListener('mousemove', handleMouseMove)
                      document.addEventListener('mouseup', handleMouseUp)
                    }}
                  />
                  <Suspense fallback={null}><RightEditorContainer /></Suspense>
                </>
              )}
            </div>
          )}

          {/* 右侧编辑栏 - 移动端半屏侧滑（★ 左滑右参考编辑器可全屏） */}
          {isMobile && rightEditorOpen && (
            <div
              className={`absolute right-0 top-0 bottom-0 z-50 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 ${
                rightEditorFullscreen ? 'w-full' : 'w-1/2'
              }`}
              style={{ touchAction: 'pan-y' }}
            >
              <Suspense fallback={null}><RightEditorContainer /></Suspense>
            </div>
          )}

          {/* Block reference panel - 移动端不显示 */}
          {!isMobile && blockReferencePanelOpen && (
            <div className="w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Suspense fallback={null}><BlockReferencePanel /></Suspense>
            </div>
          )}

          {/* 知识图谱面板 - 仅桌面端（移动端不需要图谱功能） */}
          {!isMobile && graphPanelOpen && (
            <div className="w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Suspense fallback={null}><KnowledgeGraphPanel /></Suspense>
            </div>
          )}
        </div>
      </div>

      {/* 移动端键盘浮动工具栏 - 全局挂载，作用于焦点笔记 */}
      {isMobile && <Suspense fallback={null}><MobileKeyboardToolbar /></Suspense>}

      {/* Global notifications */}
      <NotificationContainer />
      
      {/* 批量删除模态框 */}
      <BatchDeleteModal
        onDeleteNote={handleBatchDeleteNote}
        onOpenNote={(noteId) => handleNoteClick(noteId)}
        onMoveToRoot={handleMoveToRoot}
      />

      {/* 速记库删除强确认弹窗（速记模式核心容器，防误删触发自动重建循环） */}
      <ConfirmDialog
        open={sujiDeleteConfirm !== null}
        title='删除"速记库"？'
        content={`速记库是速记模式的核心容器${sujiDeleteConfirm && sujiDeleteConfirm.childCount > 0 ? `，包含 ${sujiDeleteConfirm.childCount} 条速记笔记（将一并移入回收站）` : ''}。删除后下次进入速记模式会自动创建新的速记库。`}
        confirmText='移入回收站'
        danger
        loading={sujiDeleteLoading}
        onConfirm={handleConfirmSujiDelete}
        onCancel={() => setSujiDeleteConfirm(null)}
      />

      {/* 密码初始化弹窗 */}
      <LockPasswordInitModal
        noteId={selectedNoteId || ''}
        noteTitle={selectedNoteTitle}
        isOpen={lockInitModalOpen}
        onClose={() => setLockInitModalOpen(false)}
        onSuccess={handleLockInitSuccess}
        onLockNote={(noteId) => dispatch(lockNote(noteId) as any)}
      />

      {/* 取消上锁密码验证弹窗 */}
      <UnlockNoteModal
        noteId={selectedNoteId || ''}
        noteTitle={selectedNoteTitle}
        isOpen={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        onSuccess={handleUnlockSuccess}
      />

      {/* 解锁会话管理器 */}
      <LockSessionManager />

      {/* 解锁状态指示器 */}
      <LockStatusIndicator />

      {/* 配额超限提示弹窗 */}
      <QuotaExceededModal />
    </div>
    </EditorRegistryProvider>
  )
}

export default Layout